<template>
  <var-locale-provider class="app" :locale="locale.GetLocale" :messages="messages">
    <LoginComponent ref="LoginRef"></LoginComponent>
    <ResetPasswordComponent ref="ResetPasswordRef"></ResetPasswordComponent>
    <div class="app-content-content">
      <div class="app-content-body">
        <AIPopupComponent v-if="IsMobile"></AIPopupComponent>
        <HeaderComponent v-if="IsMobile"></HeaderComponent>
        <HeaderPCComponent v-else></HeaderPCComponent>
        <MarqueeWrapperComponent></MarqueeWrapperComponent>
        <AIOrderTaskWindow></AIOrderTaskWindow>
        <router-view />
        <var-back-top :duration="300" />
        <BottomComponent></BottomComponent>
      </div>
    </div>
  </var-locale-provider>
</template>

<script setup lang="ts">
import { Locale, StyleProvider } from '@varlet/ui'
import { useLocaleStore } from "@/store/locale";
import HeaderComponent from './components/Phone/HeaderComponent.vue';
import HeaderPCComponent from "@/components/PC/HeaderComponent.vue";
import { ref, onMounted, getCurrentInstance, onBeforeUnmount, computed, provide, watch } from 'vue';
import { useThemeStore } from './store/theme';
import BottomComponent from './components/Phone/BottomComponent.vue';
import LoginComponent from './components/LoginComponent.vue';
import SignalRService from './typescript/Services/SignalRService';
import Tools from './utils/Tools';
import { ColorEnum } from './utils/SuperEnum';
import Global from './utils/Global';
import AIPopupComponent from './components/AiComponents/AIPopupComponent.vue';
import AIOrderTaskWindow from './components/AiComponents/AIOrderTaskWindow.vue';
import ResetPasswordComponent from './components/ResetPasswordComponent.vue';
import MarqueeWrapperComponent from './components/UI/MarqueeWrapperComponent.vue';
import { useSystemConfigStore } from './store/SystemConfigStore';
import { useUserStore } from './store/UserStore';
import { useTaskStatisticeStore } from './store/TaskStatisticeStore';
import { useAiOrderStore } from './store/AiOrderTaskStore';
import { useWindowSize } from '@vueuse/core';
import { useTradeDetailStore } from './store/TradeDetailStore';
import { useProductStore } from './store/ProductStore';
import { useTransactionStore } from './store/TransactionStore';
import { fetchLoginFromTokenAsync } from './typescript/Services/Api/AuthApi';
import router from './router';
const messages = Locale.messages.value;
const { width } = useWindowSize();
const LoginRef = ref(null);
const ResetPasswordRef = ref<InstanceType<typeof ResetPasswordComponent>>(); //重置密码组件
const locale = useLocaleStore();
const userStore = useUserStore();
const aiOrderStore = useAiOrderStore();

locale.SetLocale(locale.GetLocale);
// 判断是否是手机端
const IsMobile = computed(() => width.value < 768);

//每日行情
const tradeDetail = useTradeDetailStore();

const systemConfigStore = useSystemConfigStore();

const useTaskStore = useTaskStatisticeStore();

const productStore = useProductStore(); //产品数据


userStore.InitAsync();

//#region 全局样式

const themeStore = useThemeStore();
themeStore.setTheme(themeStore.getThemeName);

//#endregion

watch(() => IsMobile.value, (_n, _o) => {
  applyViewportScale();
});

watch(() => systemConfigStore.config, () => {
  const title = globalThis.document.querySelector("title");
  if (title) {
    title.innerText = systemConfigStore.config?.SystemName ?? '';
  }
}, {
  immediate: true
});

watch(() => userStore.getUser.Uid, (_n, _o) => {
  if (_n) {
    SignalRService.Instance.JoinGroupAsync(_n).then(() => {
      Tools.ShowLog("SignalR", "已加入用户组", ColorEnum.Success);
      //加群群组
      useTaskStore.InitEventAsync();
    }).catch(() => {
      Tools.ShowLog("SignalR", "加入用户组失败", ColorEnum.Error);
    });
  }
});


watch(() => productStore.CurrentProductCode, (newValue, oldValue) => {
  if (Tools.IsNotNull(newValue)) {
    productStore.LoadKlink24HDataAsync(newValue); //加载24H数据
    tradeDetail.LoadDataAysnc(newValue);

  }
});

function ShowLogin() {
  (LoginRef.value as any).ShowLogin();
}

function ShowResetPassword() {
  (ResetPasswordRef.value as any).ShowResetPassword();
}

/**
 * 初始化消息通知事件
 */
function InitMessageEvent() {
  //#region  系统配置变更

  SignalRService.Instance.AddEvent("ConfigUpdated", () => {
    systemConfigStore.GetSystemConfigAsync();
  });

  //#endregion

  //#region  用户信息变更

  SignalRService.Instance.AddEvent("UserInfoUpdated", () => {
    userStore.UpdateUserDataAsync();
  });

  //#endregion

  //#region  用户余额变更

  SignalRService.Instance.AddEvent("UserBalanceUpdated", () => {
    userStore.UpdateUserDataAsync();
  });

  //#endregion


  //#region  任务执行事件

  SignalRService.Instance.AddEvent("TaskWorkEvent", (d: {
    /**
     * 任务名称
     */
    TaskName: "SpotTradeTaskVO" | "OptionTaskVO" | "FuturesTradeTaskVO" | "DirectlyModifyAITaskVO",
    /**
     * 任务单号
     */
    TaskOrderNumber: string,
    /**
     * 累计金额
     */
    AccumulatedAmount: number,
    /**
     * 任务总数
     */
    TotalTaskCount: number,
    /**
     * 任务开始时间
     */
    TaskStartTime: number,
    /**
     * 任务结束时间
     */
    TaskOverTime: number,
    /**
     * 是否完成
     */
    IsFinish: boolean
  }) => {
    switch (d.TaskName) {
      case "DirectlyModifyAITaskVO": //AI任务
        const find = aiOrderStore.data.list.find(v => v.OrderNumber === d.TaskOrderNumber);
        if (find) {
          find.TotalAmount = d.AccumulatedAmount;
          find.TotalTaskCount = d.TotalTaskCount;
          find.StartTime = d.TaskStartTime;
          find.OverTime = d.TaskOverTime && d.TaskOverTime > 0 ? d.TaskOverTime : Tools.GetNowTime;
          if (d.IsFinish) {
            find.EffectStatus = 'finish';
            find.OverTime = d.TaskOverTime;
          }
        }
        break;
      case "OptionTaskVO": //期权
        break;
      case "FuturesTradeTaskVO": //合约
        break;
      case "SpotTradeTaskVO": //现货
        const transactionStore = useTransactionStore();
        transactionStore.GetFuturesTradeRecordsOpenListForUser();
        break;
    }

  });

  //#endregion

  //#region 获取实时买卖数据

  tradeDetail.InitEvent();

  //#endregion

}




/**
 * 设置网站图标
 * @param url 图标地址
 */
function SetFavicon(url: string) {
  url = Global.GetResourceURL(url);
  let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = url;
}

const DESIGN_WIDTH = 425;
// 定义自动缩放函数
function applyViewportScale() {
  if (IsMobile.value) {
    // ✅ 移动端：等比缩放适配
    const scale = width.value / DESIGN_WIDTH;
    setViewportMeta(scale);
  } else {
    setViewportMeta(1);
  }
}

function setViewportMeta(scale: number) {
  let viewport = document.querySelector('meta[name="viewport"]')
  if (!viewport) {
    viewport = document.createElement('meta')
    viewport.setAttribute('name', 'viewport')
    document.head.appendChild(viewport)
  }
  else {
    viewport.setAttribute(
      'content',
      `width=${DESIGN_WIDTH},initial-scale=${scale}`
    );
  }
  Tools.ShowLog("App", `设置缩放比例：${scale}`);
}

onMounted(() => {

  //#region  其他基础数据初始化
  const a = getCurrentInstance();
  Global.app = a;

  //#region  自定义缩放
  applyViewportScale();
  //#endregion
  const query = Object.fromEntries(new URLSearchParams(window.location.search))
  if ('autotoken' in query) {
    fetchLoginFromTokenAsync(query.autotoken).then(resp => {
      if (resp.CheckIsOk(false)) {
        const d = resp.data!;
        Global.JwtToken = d.token;
        Global.RefreshToken = d.refreshToken;
        router.push('/');
      }
    });
  }

  if ('share_uid' in query) {
    Tools.SetLocalStorage('EXCHANGE_SHARE_UID', query.share_uid);
  }




  SetFavicon(systemConfigStore.getConfig?.SystemLogo ?? '');
  //#endregion

  //#region  进行数据初始化
  /**
   * 初始化所有监听器
   */
  InitMessageEvent();

  //初始化配置
  systemConfigStore.GetSystemConfigAsync();

  userStore.InitAsync();

  userStore.UpdateUserDataAsync();

  //初始化产品
  productStore.GetProductListAsync();
  productStore.InitEvent();
  //#endregion

});

onBeforeUnmount(() => {
  window.removeEventListener('resize', applyViewportScale);
});


provide("IsMobile", IsMobile);



defineExpose({
  ShowLogin,
  ShowResetPassword
})

</script>

<style lang="scss">
@import url(//at.alicdn.com/t/c/font_4929564_6biwrepek8c.css);

* {
  box-sizing: border-box !important;
}

.yellow {
  color: #fdbe19 !important;
}

.green {
  color: #1fc65b !important;
}

.red {
  color: red !important;
}

.icon {
  font-family: "iconfont";
}


body {
  padding: 0px;
  margin: 0px;
  transition: background-color .25s, color .25s;
  color: var(--color-text);
  background-color: var(--color-body);
  color-scheme: var(--color-scheme);
  height: 100%;
  width: 100%;
  overflow-x: hidden;
}

#app,
.app {
  height: 100%;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.row {
  display: flex;
}

.lie {
  display: flex;
  flex-direction: column;
}


.hui-color {
  color: #868c9a !important;
}

.red-amount,
.red-color {
  color: red !important;
}

.green-amount,
.green-color {
  color: #2ebd85 !important;
}

.app-content-content {
  display: flex;
  position: relative;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  .app-content-body {
    display: flex;
    flex-direction: column;
    height: 100%;
    flex-grow: 1;
    overflow: hidden;

    &::-webkit-scrollbar {
      width: 0px;
      height: 0px;
    }
  }
}
</style>
